import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, RotateCcw, Braces } from "lucide-react";

const TemplateList = ({ templates, current, onSelect }) => (
  <div className="bg-white border border-gray-200 divide-y divide-gray-100 self-start" data-testid="email-templates-list">
    {templates.map((t) => (
      <button key={t.key} data-testid={`email-template-${t.key}`} onClick={() => onSelect(t.key)} className={`w-full text-left px-4 py-3 transition-colors hover:bg-[#F8F6F0] ${current === t.key ? "bg-[#F8F6F0] border-l-4 border-[#E0C173]" : "border-l-4 border-transparent"}`}>
        <div className="text-sm font-medium text-[#1B2A4A]">{t.label}</div>
        <div className="text-[11px] uppercase tracking-wider text-[#475569] mt-0.5">{t.recipient}{t.customized && <span className="ml-2 text-[#C9A85A]">· personnalisé</span>}</div>
      </button>
    ))}
  </div>
);

const VariableChips = ({ variables, onInsert }) => (
  <div className="flex flex-wrap gap-1.5 items-center">
    <span className="text-xs text-[#475569] flex items-center gap-1 mr-1"><Braces className="w-3.5 h-3.5" /> Variables :</span>
    {variables.length === 0 && <span className="text-xs text-gray-400">aucune</span>}
    {variables.map((v) => (
      <button key={v} type="button" data-testid={`var-chip-${v}`} onClick={() => onInsert(`{{${v}}}`)} className="text-xs font-mono px-2 py-1 bg-[#1B2A4A]/5 text-[#1B2A4A] hover:bg-[#E0C173] transition-colors rounded-full">{`{{${v}}}`}</button>
    ))}
  </div>
);

const Editor = ({ tpl, onSaved, onDirty }) => {
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body);
  const [preview, setPreview] = useState({ subject: "", html: "" });
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setSubject(tpl.subject); setBody(tpl.body); }, [tpl]);
  useEffect(() => { onDirty(subject !== tpl.subject || body !== tpl.body); }, [subject, body, tpl]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => { if (subject && body) api.post("/admin/email-templates/preview", { subject, body }).then((r) => setPreview(r.data)).catch(() => {}); }, 350);
    return () => clearTimeout(t);
  }, [subject, body]);

  const insert = (txt) => {
    const el = ref.current; const s = el?.selectionStart ?? body.length; const e = el?.selectionEnd ?? body.length;
    setBody(body.slice(0, s) + txt + body.slice(e));
    setTimeout(() => { el?.focus(); el?.setSelectionRange(s + txt.length, s + txt.length); }, 0);
  };
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/email-templates/${tpl.key}`, { subject, body }); toast.success("Modèle enregistré"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Rétablir le modèle d'origine ?")) return;
    await api.delete(`/admin/email-templates/${tpl.key}`); toast.success("Modèle d'origine rétabli"); onSaved();
  };

  return (
    <div className="space-y-4 min-w-0" data-testid="template-editor">
      <div className="bg-white border border-gray-200 p-5 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#475569]">Objet</label>
          <Input data-testid="template-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-none mt-1.5 border-gray-300" />
        </div>
        <VariableChips variables={tpl.variables} onInsert={insert} />
        <p className="text-xs text-[#475569]">Astuce : <code className="font-mono">{"{{#message}}…{{/message}}"}</code> n'affiche le bloc que si la variable est renseignée. Le corps accepte le HTML ; l'en-tête et le pied de page PROAC sont ajoutés automatiquement.</p>
        <div>
          <label className="text-xs uppercase tracking-wider text-[#475569]">Corps du message</label>
          <textarea ref={ref} data-testid="template-body" value={body} onChange={(e) => setBody(e.target.value)} rows={14} className="w-full mt-1.5 border border-gray-300 p-3 font-mono text-xs leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#E0C173]" />
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {tpl.customized && <Button data-testid="template-reset" variant="outline" onClick={reset} className="rounded-full"><RotateCcw className="w-4 h-4 mr-1.5" /> Modèle d'origine</Button>}
          <Button data-testid="template-save" disabled={busy} onClick={save} className="rounded-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white px-6"><Save className="w-4 h-4 mr-1.5" /> Enregistrer</Button>
        </div>
      </div>
      <div className="bg-white border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-100 text-sm"><span className="text-xs uppercase tracking-wider text-[#475569] mr-2">Aperçu (données d'exemple)</span><strong data-testid="template-preview-subject" className="text-[#1B2A4A]">{preview.subject}</strong></div>
        <iframe title="Aperçu modèle" data-testid="template-preview-iframe" srcDoc={preview.html} sandbox="" className="w-full h-[520px] bg-white" />
      </div>
    </div>
  );
};

export default function EmailTemplatesPanel({ templates, reload }) {
  const [current, setCurrent] = useState(null);
  const [dirty, setDirty] = useState(false);
  const tpl = templates.find((t) => t.key === current) || templates[0];
  if (!tpl) return null;
  const select = (key) => {
    if (key === tpl.key || (dirty && !window.confirm("Des modifications non enregistrées seront perdues. Continuer ?"))) return;
    setCurrent(key);
  };
  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-6" data-testid="email-templates-panel">
      <TemplateList templates={templates} current={tpl.key} onSelect={select} />
      <Editor tpl={tpl} onSaved={reload} onDirty={setDirty} />
    </div>
  );
}
